import { useEffect, useState } from "react";

import { ITileLayerInfo } from "../../MapComponents/layers";
import { ILeafletTileLayerSnapshotGeneratorOptions, LeafletSnapshotGenerator } from "../../MapComponents/Leaflet/SnapshotGenerator/leafletSnapshotGenerator";
import { ITileCache, ITileCacheSeeder } from "../../MapComponents/TileCache/types";
import { IMapLayerSnapshotGenerator } from "../../MapComponents/snapshotGenerator";
import { IConfig, IMapSettings, ISettings } from "../../../Settings/settingsTypes";
import { useAction } from "../../../Lib/Actions/useAction";
import { IAction } from "../../../Lib/Actions/actions";
import { IPlaceSearchResult } from "../../Search/placeSearchTypes";
import { IMapStateService } from "../../../Settings/serviceTypes";
import { ILog } from "../../../Lib/Log/types";


export interface IMainMap {
    config: IConfig,
    mapState: IMapStateService;
    mapSettings: IMapSettings,
    maxBounds: L.LatLngBounds,

    tileLayers: ITileLayerInfo[];

    tileCache: ITileCache;
    tileCacheSeeder: ITileCacheSeeder | null;
    mapSnapshotGenerator: IMapLayerSnapshotGenerator;

    currentTileLayer: ITileLayerInfo;

    openSettingsAction: IAction<{ map: L.Map }, void> ;
    openDownloadDataAction: IAction<{ map: L.Map, urlTemplate: string, urlSubdomains: string[] }, void>;
    openSearchPlacesAction: IAction<{ map: L.Map, biasBounds?: L.LatLngBounds | null }, IPlaceSearchResult>;
    openChangeLayerAction: IAction<void, ITileLayerInfo>;
    openLocationInfoAction: IAction<void, void>;
};

const useMainMap = (
    config: IConfig,
    mapStateService: IMapStateService,
    settings: ISettings,
    mapSnapshotGeneratorOptions: ILeafletTileLayerSnapshotGeneratorOptions,
    tileCache: ITileCache,
    cacheSeeders: ITileCacheSeeder[],
    log: ILog
): IMainMap => {

    const mapSnapshotGenerator = new LeafletSnapshotGenerator(mapSnapshotGeneratorOptions, log);

    const tileCacheSeeder: ITileCacheSeeder | null = cacheSeeders.find(cs => cs.id === settings.tileCache.activeCacheSeederId) ?? cacheSeeders.find(cs => cs.isSupported() === true) ?? null;

    //Interface
    const initialTitleLayer = mapStateService.state?.layerId ?? settings.map.defaultLayerId;
    const [currentTileLayer, internalSetCurrentTileLayer] = useState<ITileLayerInfo>(config.tileLayers.find(tl => tl.id === initialTitleLayer) ?? config.tileLayers[0]);

    //Actions
    const openSettingsAction = useAction<{ map: L.Map }, void>();
    const openDownloadDataAction = useAction<{ map: L.Map, urlTemplate: string, urlSubdomains: string[] }, void>();
    const openSearchPlacesAction = useAction<{ map: L.Map, biasBounds?: L.LatLngBounds | null }, IPlaceSearchResult>();
    const openChangeLayerAction = useAction<void, ITileLayerInfo>();
    const openLocationInfoAction = useAction<void, void>();

    useEffect(() => {

        if (!!openSearchPlacesAction.result) {
            openSearchPlacesAction.params!.map.fitBounds(openSearchPlacesAction.result.boundingBox);
        }

    }, [openSearchPlacesAction.params, openSearchPlacesAction.result])
    
    useEffect(() => {

        if (!!openChangeLayerAction.result) {
            const newLayer = config.tileLayers.find(tl => tl.id === openChangeLayerAction.result?.id);

            if (!!newLayer)
                internalSetCurrentTileLayer(newLayer);
        }

    }, [config.tileLayers, openChangeLayerAction.result])


    return {
        config,
        mapState: mapStateService,
        mapSettings: settings.map,
        maxBounds: config.maxBounds,
        tileLayers: config.tileLayers,
        tileCache,
        tileCacheSeeder,
        mapSnapshotGenerator,
        currentTileLayer,
        openSettingsAction, openDownloadDataAction, openSearchPlacesAction, openChangeLayerAction, openLocationInfoAction
    };
};

export default useMainMap;